import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogContent } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import { useSnackbar, VariantType } from 'notistack';
import * as R from 'ramda';
import { ApplicationContext } from '../../context/Contexts';
import { useAppSelector } from '../../store/configureStore';
import { CaseSettingsState, AppState } from '../../store';
import { useStyles } from '../../custom-forms';
import AlternativeCalculationHeader from '../alternative-calculation/components/AlternativeCalculationHeader';
import Referentials from '../../helpers/Referentials.json';
import { ReferentialCode, CalculationSectionCodeEnum } from '../../helpers/Constants';
import { IReferential } from '../../interfaces/IReferential';
import { Organization } from '../../interfaces/Organization';
import { PartToBeOrdered } from '../../interfaces/Order';
import {
  CaseCalculationAlternativePart,
  CaseCalculationHistory,
  CaseCalculationPart,
  TariffPercentResponse
} from '../../interfaces/Case';
import { WorkflowSupplier } from '../../interfaces/Workflow';
import { mapAlternativePartToCalculationPart } from '../../utils/calculationPartUtils';
import { CalculationFlagsProvider } from '../alternative-calculation/context/CalculationFlagsContext';
import { CalculationActions } from '../alternative-calculation/types/alternative-calculation.types';
import { partitionEditedCalculationPart } from '../alternative-calculation/utils/calculation.utils';
import { ORDER_PART_WORKFLOW_CALCULATION } from './order.utils';
import { useCalculationPartsActions } from '../alternative-calculation/hooks/useCalculationPartsActions';
import UpdateOrderPartContent from './UpdateOrderPartContent';

export interface ExternalUpdateOrderPartProps {
  partDetails: PartToBeOrdered;
  currency: string;
  workflowId: number;
  suppliers: Organization[];
  additionTariff: TariffPercentResponse;
  discountTariff: TariffPercentResponse;
  onSave: () => void;
  onClose: () => void;
}

const UpdateOrderPartDialog: React.FC<ExternalUpdateOrderPartProps> = (props) => {
  const { partDetails, suppliers, additionTariff, discountTariff, onSave, onClose } = props;

  const context = useContext(ApplicationContext);
  const caseService = context.caseService;
  const orderService = context.orderService;
  const referentialService = context.referentialService;
  const translatorService = context.translatorService;
  const { enqueueSnackbar } = useSnackbar();

  const caseSettingsState = useAppSelector((s) => s.caseSettings) as CaseSettingsState;
  const appState = useAppSelector((s) => s.app) as AppState;
  const appUser = appState.appUser;
  const workflow = caseSettingsState.caseSettings!.workflow;

  const variantsEnabled = false;
  const isOffline = false;
  const ownStockOrganization: Organization | null = null;
  const workflowCalculation = ORDER_PART_WORKFLOW_CALCULATION;

  const editedPartId = partDetails.caseCalculationPartId;

  const [isLoading, setIsLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [qualities, setQualities] = useState<IReferential[]>([]);
  const [sections, setSections] = useState<IReferential[]>([]);
  const [calculationParts, setCalculationParts] = useState<CaseCalculationPart[]>([]);
  const [nonStockSuppliers, setNonStockSuppliers] = useState<WorkflowSupplier[]>([]);

  const onNotify = (message: string, variant: VariantType) => enqueueSnackbar(message, { variant });
  const c = useStyles();
  const country = appUser?.organization?.country ?? null;

  const getPartsLocationOrganizationId = (): number | null => {
    return (
      partDetails.caseCalculationHistory?.partsLocationOrganizationId ?? appUser!.organizationId
    );
  };

  const {
    toggleExpand,
    interogate,
    interogateAlternativeParts,
    handleAlternativePartSelect,
    toggleAlternativePartSelection,
    clearRecommendation,
    unselectPart,
    unselectAll,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    validateSelectedParts
  } = useCalculationPartsActions({
    calculationParts,
    setCalculationParts,
    additionTariff,
    discountTariff,
    workflow,
    workflowCalculation,
    ownStockOrganization,
    variantsEnabled,
    nonStockSuppliers,
    setNonStockSuppliers,
    getPartsLocationOrganizationId,
    getInterrogationOrganizationId: () =>
      partDetails.caseCalculationHistory?.partsLocationOrganizationId ?? null,
    interrogationSupplier: null,
    reuseGroupedUniqueId: false
  });

  // Single source for the edited part; reused by render, save and selection handlers.
  const part = calculationParts.find((p) => p.id === editedPartId) ?? null;

  const activeSuppliers = useMemo(
    () => (suppliers || []).filter((item) => item.isActive === true),
    [suppliers]
  );

  const loadData = async () => {
    const refQualityPart = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CarPartCategory
    );

    const [qualityParts, calculationParts, sections] = await Promise.all([
      referentialService.Get(refQualityPart!.baseUrl),
      partDetails.caseCalculationHistoryId
        ? caseService.GetCaseCalculationParts(partDetails.caseCalculationHistoryId)
        : ([] as CaseCalculationPart[]),
      caseService.GetCalculationSections()
    ]);

    calculationParts.forEach((item) => {
      item.alternativeParts = item.alternativeParts ?? [];
    });

    const { calculationPartsCopy, selectedCalculationPart } = partitionEditedCalculationPart(
      calculationParts,
      partDetails.groupedUniqueId,
      partDetails.caseCalculationPartId
    );

    setQualities(qualityParts);
    setSections(sections);
    setCalculationParts(calculationPartsCopy.concat([selectedCalculationPart!]));
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noLoop = () => undefined;

  const calculationActions: CalculationActions = {
    registerRow: noLoop,
    toggleExpand,
    interogate,
    interogateAlternativeParts,
    interogateAll: noLoop,
    resetAlternatives: noLoop,
    toggleSelect: toggleAlternativePartSelection,
    setRecommended: (partId: number, alternativePart: CaseCalculationAlternativePart) =>
      handleAlternativePartSelect(alternativePart.id, partId, alternativePart),
    clearRecommendation,
    unselectPart,
    unselectAll,
    deletePart: noLoop,
    sendNotificationToSupport: () => Promise.resolve(),
    addAlternativePart: noLoop,
    importOriginalParts: noLoop,
    importAlternativeParts: noLoop,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    canOptimize: () => false,
    openOptimize: noLoop,
    optimizeByBestPrice: noLoop,
    optimizeByBestQuality: noLoop,
    applyOptimal: noLoop,
    isOptimizingLoading: false,
    isApplyingOptimalLoading: false,
    addCalculationPart: noLoop,
    copyCalculationPart: noLoop
  };

  const setCaseCalculationPartInterogations = (
    calcPart: CaseCalculationPart,
    interogateList: CaseCalculationAlternativePart[]
  ) => {
    calcPart.caseCalculationPartInterogations = [
      { interogationList: JSON.stringify(interogateList) }
    ];
  };

  const buildCalculationParts = (
    selected: CaseCalculationAlternativePart,
    idx: number,
    current: CaseCalculationPart,
    base: CaseCalculationPart[]
  ): CaseCalculationPart[] => {
    const partSection = sections.find((item) => item.code === CalculationSectionCodeEnum.PART)!;
    const result = base.map((item, i) =>
      i === idx
        ? {
            ...item,
            ...mapAlternativePartToCalculationPart(
              selected,
              current.groupedUniqueId,
              selected.excludeFromOrder
            ),
            id: partDetails.caseCalculationPartId
          }
        : item
    );

    (current.package || []).forEach((child: CaseCalculationPart) => {
      result.push({
        ...child,
        id: 0,
        caseCalculationHistoryId: partDetails.caseCalculationHistory.id,
        calculationSectionId: partSection.id,
        groupedUniqueId: current.groupedUniqueId
      } as CaseCalculationPart);
    });

    return result.map((item) => ({
      ...item,
      calculationSection: null,
      carPartCategory: null,
      alternativePartQuality: null,
      supplier: null
    }));
  };

  const save = async () => {
    const current = part;
    if (!current) {
      return;
    }
    const selected = current.alternativeParts.find(
      (item) => item.isSelectedAlternativePart && !item.isChild
    );
    if (current.alternativeParts.length === 0 || R.isNil(selected)) {
      return;
    }

    const { validated, errorString, decimalError, descriptionError, zeroPieceNrError } =
      validateSelectedParts([current]);

    // Apply the refreshed per-part flags so the offending Bucăți input turns red.
    const validatedById = new Map(validated.map((p) => [p.id, p]));
    setCalculationParts((parts) => parts.map((p) => validatedById.get(p.id) ?? p));

    if (errorString) {
      enqueueSnackbar(errorString, { variant: 'warning', style: { whiteSpace: 'pre-line' } });
      return;
    }
    if (decimalError) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'DECIMAL_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie un numar intreg fara zecimale'
        ),
        { variant: 'warning' }
      );
      return;
    }
    if (descriptionError) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'ALTERNATIVE_PART_DESCRIPTION_ERROR_MSG',
          'Va rugam completati descrierea piesei alternative!'
        ),
        { variant: 'warning' }
      );
      return;
    }
    if (zeroPieceNrError) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'VALIDATION_QTY_ZERO_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie mai mare decat 0!'
        ),
        { variant: 'warning', autoHideDuration: 5000 }
      );
      return;
    }

    try {
      setExecuting(true);
      const orderPart: PartToBeOrdered = {
        ...partDetails,
        ...mapAlternativePartToCalculationPart(
          selected,
          current.groupedUniqueId,
          selected.excludeFromOrder
        ),
        originalPrice: current.originalPrice,
        carPartCategoryId: current.carPartCategoryId,
        pieceNr: current.pieceNr,
        package: current.package
      } as PartToBeOrdered;
      orderPart.alternativePartQuality = null;
      orderPart.carPartCategory = null;
      orderPart.supplier = null;

      const idx = calculationParts.findIndex((x) => x.id === editedPartId);
      if (idx === -1) {
        setExecuting(false);
        return;
      }

      const parts = buildCalculationParts(selected, idx, current, calculationParts);
      setCaseCalculationPartInterogations(parts[idx], current.alternativeParts);

      await caseService.UpdateCalculationPartsAndCasePartToBeOrdered(
        parts,
        '',
        '',
        partDetails.caseCalculationHistory.id,
        partDetails.caseCalculationHistory.partsLocationOrganizationId,
        appUser!.organizationId,
        appUser!.organizationId,
        appUser!.organization!.name,
        (appUser!.organization!.isGtmManaged &&
          caseSettingsState.caseSettings?.workflow?.isCarfixIntegration) ??
          false
      );

      orderPart.caseCalculationHistory = null as unknown as CaseCalculationHistory;
      await orderService.UpdatePartToBeOrdered(orderPart);

      onSave();
      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    } finally {
      setExecuting(false);
    }
  };

  const orderedInfoRows: [string, React.ReactNode][] = [
    [
      translatorService.Tranlate(
        'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_SUPLIER_LABEL',
        'Furnizor'
      ),
      partDetails.supplier?.name
    ],
    [
      translatorService.Tranlate(
        'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_OWNER_LABEL_ACRONYM',
        'Producator'
      ),
      partDetails.producer
    ],
    [
      translatorService.Tranlate(
        'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_DELIVERY_LABEL',
        'Livrare'
      ),
      partDetails.deliveryTime
    ],
    [
      translatorService.Tranlate('ORDER_PART_DETAILS_STOCK_LABEL', 'Disponibil'),
      partDetails.availableStock
    ],
    [
      translatorService.Tranlate(
        'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_STOCK_LOCATION_LABEL',
        'Locatie'
      ),
      partDetails.location
    ]
  ];

  const orderedInfo = (
    <div>
      {orderedInfoRows.map((row) => (
        <div key={row[0]}>
          {row[0]}: {row[1] != null && row[1] !== '' ? row[1] : '-'}
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open onClose={onClose} fullScreen fullWidth>
      <AlternativeCalculationHeader
        onClose={onClose}
        title={translatorService.Tranlate('ORDER_CHANGE_PART_DETAILS_TITLE', 'Modifica date piesa')}
        showReference={false}
      />
      <DialogContent className={c.pageBg} style={{ padding: 0 }}>
        <Box className={c.pageBg} style={{ minHeight: '100%', fontFamily: 'Montserrat' }}>
          {isLoading || !part ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ minHeight: '60vh' }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, width: '100%' }}>
                <ScaleLoader color={'var(--primary)'} loading={true} />
              </div>
            </Box>
          ) : (
            <CalculationFlagsProvider variantsEnabled={variantsEnabled} isOffline={isOffline}>
              <UpdateOrderPartContent
                part={part}
                qualities={qualities}
                suppliers={activeSuppliers}
                workflowCalculation={workflowCalculation}
                actions={calculationActions}
                onNotify={onNotify}
                variantsEnabled={variantsEnabled}
                isOffline={isOffline}
                country={country}
                orderedInfo={orderedInfo}
                onSave={save}
                executing={executing}
              />
            </CalculationFlagsProvider>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateOrderPartDialog;
